import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate, useLocation } from "react-router-dom"; // 👈 ADD useLocation
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo.webp";
import { getUser, me } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import AuthDialog from "@/components/AuthDialog";
import { memoryUsage } from "process";

const MainNav = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const sessionUser = me();
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const { studioName } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation(); // 👈 Get the current location object

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return (
        user?.firstName.charAt(0).toUpperCase() +
        user?.lastName.charAt(0).toUpperCase() || "??"
      );
    } else if (user?.email) {
      return (
        user?.email.split("@")[0].slice(0, 2).toUpperCase() || "??"
      );
    }
    return "--";
  };

  const handleLogout = () => {
    const sessionUser = me();
    localStorage.removeItem("currentUser");
    sessionStorage.clear();
    if (studioName) {
      window.location.href = "/studio/" + studioName;
    } else if (sessionUser?.isAdmin) {
      window.location.href = "/admin/login";
    } else {
      window.location.href = "/";
    }
  };

  const handleAuthSuccess = () => {
    setIsAuthOpen(false);
    navigate(0);
  };

  useEffect(() => { }, []);

  // Function to determine if a link is active
  const isActive = (path) => {
    // Check if the current pathname starts with the given path (e.g., /admin/dashboard vs /admin/clients)
    return location.pathname.includes(path);
  };

  const baseLinkClasses = "px-3 py-2 rounded-md font-semibold transition-colors text-sm";
  const activeLinkClasses = "bg-gray-100 text-primary";
  const inactiveLinkClasses = "text-gray-600 hover:bg-gray-50 hover:text-primary";

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100"> {/* Added subtle border */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center py-4">
          {/* Left Section: Logo + Admin Menu */}
          <div className="flex items-center space-x-6 mb-4 md:mb-0">
            <h1 className="text-2xl font-bold text-primary">
              <a href="/">
                <img
                  src={logo}
                  alt="FitCalendar"
                  className="h-8 object-contain"
                />
              </a>
            </h1>

            {/* ✅ Show Admin Menu if user is admin */}
            {sessionUser?.isAdmin && (
              <div className="hidden md:flex items-center space-x-1"> {/* Increased space-x for better modern look */}
                <Link
                  to="/admin/dashboard" // Adjusted to use /admin/dashboard as seen in the URL
                  className={`${baseLinkClasses} ${isActive("dashboard") ? activeLinkClasses : inactiveLinkClasses
                    }`}
                >
                  {t("nav.dashboard")}
                </Link>
                <Link
                  to="/admin/clients" // Adjusted to use /admin/clients
                  className={`${baseLinkClasses} ${isActive("clients") ? activeLinkClasses : inactiveLinkClasses
                    }`}
                >
                  {t("nav.clients")}
                </Link>
              </div>
            )}
          </div>

          {/* Right Section: Language, Auth, Avatar */}
          <div className="flex items-center space-x-4">
            <LanguageSelector />
            {user?.email ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Avatar className="cursor-pointer hover:opacity-80">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem className="cursor-default">
                    <span className="text-sm font-medium">{user?.email}</span>
                  </DropdownMenuItem>

                  {/* Normal user options */}
                  {!sessionUser?.isAdmin && (
                    <>
                      <DropdownMenuItem>
                        <Link to="/client-dashboard" className="w-full">
                          {t("nav.myBookings")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Link to="/account-settings" className="w-full">
                          {t("nav.accountSettings")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Link to="/change-password" className="w-full">
                          {t("nav.changePassword")}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer"
                  >
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              !sessionUser?.isAdmin &&
              studioName && (
                <Button variant="outline" onClick={() => setIsAuthOpen(true)}>
                  {t("nav.login")}
                </Button>
              )
            )}
          </div>
        </div>

        <AuthDialog
          isOpen={isAuthOpen}
          onClose={() => setIsAuthOpen(false)}
          onSuccess={handleAuthSuccess}
        />
      </div>
    </nav>
  );
};

export default MainNav;